import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase, getAdminEmail } from "../api";

export default function BrokerCarrierManagement() {
  const nav = useNavigate();
  const apiBase = useMemo(() => getApiBase(), []);
  const adminEmail = useMemo(() => getAdminEmail(), []);
  
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL"); // ALL, PENDING, APPROVED, REJECTED, SUSPENDED
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCarriers();
  }, []);

  async function fetchCarriers() {
    try {
      const res = await fetch(`${apiBase}/broker/carriers`, {
        headers: { "x-admin-email": adminEmail }
      });
      if (res.ok) {
        const data = await res.json();
        setCarriers(data.carriers || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(carrierId, newStatus) {
    setUpdating(carrierId);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/broker/carriers/${carrierId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-admin-email": adminEmail
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        // Refresh carriers list
        fetchCarriers();
      } else {
        const err = await res.json();
        setError(err.error || "Failed to update status");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setUpdating(null);
    }
  }

  const filteredCarriers = carriers.filter(c => {
    const status = c.carrierProfile?.status || "PENDING";
    if (filter !== "ALL" && status !== filter) return false;
    if (search) {
      const term = search.toLowerCase();
      return (
        c.name?.toLowerCase().includes(term) ||
        c.carrierProfile?.mcNumber?.includes(term) ||
        c.carrierProfile?.dotNumber?.includes(term)
      );
    }
    return true;
  });

  const statusCounts = {
    ALL: carriers.length,
    PENDING: carriers.filter(c => c.carrierProfile?.status === "PENDING").length,
    APPROVED: carriers.filter(c => c.carrierProfile?.status === "APPROVED").length,
    REJECTED: carriers.filter(c => c.carrierProfile?.status === "REJECTED").length,
    SUSPENDED: carriers.filter(c => c.carrierProfile?.status === "SUSPENDED").length,
  };

  const statusColors = {
    PENDING: { bg: "#fef3c7", color: "#92400e", border: "#f59e0b" },
    APPROVED: { bg: "#d1fae5", color: "#065f46", border: "#10b981" },
    REJECTED: { bg: "#fee2e2", color: "#991b1b", border: "#ef4444" },
    SUSPENDED: { bg: "#e5e7eb", color: "#374151", border: "#6b7280" },
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center" }}>Loading carriers...</div>;
  }

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>🚛 Carrier Management</h1>
          <p style={{ margin: "8px 0 0", color: "#6b7280" }}>
            Approve, reject, or suspend carriers
          </p>
        </div>
        <button
          onClick={() => nav("/app/broker/carriers/create")}
          style={{
            padding: "10px 20px",
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          + Add Carrier
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: 12, 
          background: "#fee2e2", 
          color: "#991b1b", 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        display: "flex", 
        gap: 12, 
        marginBottom: 20, 
        flexWrap: "wrap",
        alignItems: "center"
      }}>
        {["ALL", "PENDING", "APPROVED", "REJECTED", "SUSPENDED"].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              padding: "8px 16px",
              background: filter === status ? "#2563eb" : "#f3f4f6",
              color: filter === status ? "white" : "#374151",
              border: "none",
              borderRadius: 20,
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 13
            }}
          >
            {status} ({statusCounts[status]})
          </button>
        ))}
        
        <input
          type="text"
          placeholder="Search by name, MC#, or DOT#..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "8px 16px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            width: 250,
            marginLeft: "auto"
          }}
        />
      </div>

      {/* Pending Alert */}
      {statusCounts.PENDING > 0 && (
        <div style={{
          padding: 16,
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: 8,
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
          <span style={{ fontSize: 24 }}>⚠️</span>
          <div>
            <strong>{statusCounts.PENDING} carrier(s) pending approval</strong>
            <div style={{ fontSize: 13, color: "#92400e" }}>
              Review and approve carriers to allow them to bid on loads
            </div>
          </div>
          <button
            onClick={() => setFilter("PENDING")}
            style={{
              marginLeft: "auto",
              padding: "8px 16px",
              background: "#f59e0b",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Review Pending
          </button>
        </div>
      )}

      {/* Carriers Table */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        border: "1px solid #e5e7eb",
        overflow: "hidden"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Carrier</th>
              <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>MC / DOT</th>
              <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Location</th>
              <th style={{ padding: 12, textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>Status</th>
              <th style={{ padding: 12, textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCarriers.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
                  No carriers found
                </td>
              </tr>
            ) : (
              filteredCarriers.map(carrier => {
                const status = carrier.carrierProfile?.status || "PENDING";
                const colors = statusColors[status] || statusColors.PENDING;
                const isUpdating = updating === carrier.id;
                
                return (
                  <tr key={carrier.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>{carrier.name}</div>
                      {carrier.email && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{carrier.email}</div>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontSize: 13 }}>
                        {carrier.carrierProfile?.mcNumber && (
                          <span>MC: {carrier.carrierProfile.mcNumber}</span>
                        )}
                        {carrier.carrierProfile?.mcNumber && carrier.carrierProfile?.dotNumber && " / "}
                        {carrier.carrierProfile?.dotNumber && (
                          <span>DOT: {carrier.carrierProfile.dotNumber}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        {[carrier.city, carrier.state].filter(Boolean).join(", ") || "—"}
                      </div>
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        background: colors.bg,
                        color: colors.color,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {status}
                      </span>
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        {status === "PENDING" && (
                          <>
                            <button
                              onClick={() => updateStatus(carrier.id, "APPROVED")}
                              disabled={isUpdating}
                              style={{
                                padding: "6px 12px",
                                background: "#10b981",
                                color: "white",
                                border: "none",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: isUpdating ? "not-allowed" : "pointer",
                                opacity: isUpdating ? 0.5 : 1
                              }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => updateStatus(carrier.id, "REJECTED")}
                              disabled={isUpdating}
                              style={{
                                padding: "6px 12px",
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: isUpdating ? "not-allowed" : "pointer",
                                opacity: isUpdating ? 0.5 : 1
                              }}
                            >
                              ✕ Reject
                            </button>
                          </>
                        )}
                        {status === "APPROVED" && (
                          <button
                            onClick={() => updateStatus(carrier.id, "SUSPENDED")}
                            disabled={isUpdating}
                            style={{
                              padding: "6px 12px",
                              background: "#6b7280",
                              color: "white",
                              border: "none",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: isUpdating ? "not-allowed" : "pointer",
                              opacity: isUpdating ? 0.5 : 1
                            }}
                          >
                            Suspend
                          </button>
                        )}
                        {(status === "REJECTED" || status === "SUSPENDED") && (
                          <button
                            onClick={() => updateStatus(carrier.id, "APPROVED")}
                            disabled={isUpdating}
                            style={{
                              padding: "6px 12px",
                              background: "#10b981",
                              color: "white",
                              border: "none",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: isUpdating ? "not-allowed" : "pointer",
                              opacity: isUpdating ? 0.5 : 1
                            }}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
